import type { JSX } from "react";
import { usePurchasePaths } from "../../platform/PlatformContext";
import { useI18n } from "../i18n/I18nContext";
import { Alert } from "./Alert";
import { cx } from "./classNames";
import { explainError } from "./describeError";
import type { ErrorCause } from "./describeError";
import styles from "./ui.module.css";

/** Causes for which a retry cannot help, so no retry button is offered. */
const causesWithoutRetry: ReadonlySet<ErrorCause> = new Set(["authentication", "permission"]);

/** Properties of {@link ErrorMessage}. */
export interface ErrorMessageProps {
  /** The error to describe. */
  readonly error: unknown;
  /**
   * Repeats the failed action, for example the `refetch` of a query. Without it, or when a retry
   * cannot help (missing permission or sign-in), no retry button is shown.
   */
  readonly onRetry?: (() => void) | undefined;
  /** Whether the retry is running; the button is then disabled and says so. */
  readonly retrying?: boolean | undefined;
}

/**
 * Shows an error as a danger alert (UX-107, UX-108): the translated message, for generic messages
 * a hint by cause (offline, permission, server), the error code for support, which never breaks at
 * its hyphen, and optionally a retry button.
 *
 * @param props - Component properties.
 * @returns The alert element.
 */
export function ErrorMessage(props: ErrorMessageProps): JSX.Element {
  const { t } = useI18n();
  const purchasePaths = usePurchasePaths();
  const explanation = explainError(props.error, t, purchasePaths);
  const { onRetry } = props;
  const retryable = onRetry !== undefined && !causesWithoutRetry.has(explanation.cause);
  const alert = (
    <Alert tone="danger" live="assertive" className={styles["errorMessage"]}>
      <p>
        {explanation.message}
        {explanation.code === null ? null : (
          <>
            {" "}
            <span className={cx(styles["muted"], styles["errorCode"])}>
              {t("error.code", { code: explanation.code })}
            </span>
          </>
        )}
      </p>
      {explanation.hint === null ? null : <p>{explanation.hint}</p>}
      {retryable ? (
        <div className={styles["row"]}>
          <button
            type="button"
            className={styles["button"]}
            disabled={props.retrying === true}
            onClick={() => {
              onRetry();
            }}
          >
            {props.retrying === true ? t("error.retrying") : t("error.retry")}
          </button>
        </div>
      ) : null}
    </Alert>
  );

  return alert;
}
